import { Flex, Modal, Typography } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ContentSpinner, QueryErrorAlert } from 'src/components'
import {
  parseAllowedScopes,
  serializeAllowedScopes
} from 'src/features/workspaces/components/agents/scopeModel'
import { ScopePicker } from 'src/features/workspaces/components/agents/ScopePicker'
import { useScopeCatalog } from 'src/services/scopeCatalog'

import { useUpdateMcpConnectionScopes } from './hooks'
import { grantableScopes, serviceScopeRows } from './model'
import type { ConnectionsScope, McpConnection } from './types'

const { Text } = Typography

type Props = {
  workspaceId: number
  scope: ConnectionsScope
  connection: McpConnection
  onClose: () => void
}

/** Mounted per connection, so the draft starts from what it carries now. */
export const ConnectionScopesModal = ({
  workspaceId,
  scope,
  connection,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const { data: catalog = [], isLoading, isError, refetch } = useScopeCatalog()
  const update = useUpdateMcpConnectionScopes(workspaceId, scope)
  const [selected, setSelected] = useState(() =>
    parseAllowedScopes(connection.scopes)
  )

  const rows = serviceScopeRows(catalog, connection.service)
  const granted = grantableScopes(selected, rows)
  const saved = serializeAllowedScopes(parseAllowedScopes(connection.scopes))
  const draft = serializeAllowedScopes(granted)

  const handleSave = async () => {
    const result = await update
      .mutateAsync({ id: connection.id, scopes: draft })
      .catch(() => null)
    if (result) onClose()
  }

  return (
    <Modal
      open
      width={720}
      title={t('workspace.aiConnectors.connections.scopesTitle', {
        name: connection.name
      })}
      okText={t('workspace.aiConnectors.connections.scopesSave')}
      okButtonProps={{ disabled: granted.size === 0 || draft === saved }}
      confirmLoading={update.isPending}
      onOk={() => void handleSave()}
      onCancel={onClose}
    >
      <Flex vertical gap={12}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('workspace.aiConnectors.connections.scopesHint')}
        </Text>
        {isLoading ? (
          <ContentSpinner minHeight={120} />
        ) : isError ? (
          <QueryErrorAlert onRetry={() => void refetch()} />
        ) : (
          <ScopePicker catalog={rows} value={granted} onChange={setSelected} />
        )}
      </Flex>
    </Modal>
  )
}
